import React, { useState } from 'react';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Alert,
    Checkbox,
    FormControlLabel,
    CircularProgress,
    Typography,
} from '@mui/material';
import { useNotify, useRefresh } from 'react-admin';
import { APIUrl } from '../../common/apiConfig';

/**
 * Builds a school's database: POST /api/school/provision/{id}.
 *
 * WHY THIS IS A BUTTON AND NOT PART OF SAVE
 * -----------------------------------------
 * Creating the registry row is one INSERT. Building the database creates a
 * schema, creates a MySQL account, runs migrations and verifies a live bind --
 * a couple of seconds of work with five places to fail. If that ran inside the
 * create form, a migration failure would look like a failed save, and the
 * operator would not know whether the school exists, half exists, or does not.
 *
 * Kept separate, every outcome is legible. The school is visibly there and
 * visibly not provisioned; the button can simply be pressed again, because
 * School_provisioner::provision() resumes rather than restarting. Until it
 * succeeds, nothing can route to the school at all -- so the in-between state
 * is safe rather than merely tolerated.
 *
 * The step list is shown rather than a spinner and a tick, for the same reason:
 * when step 5 fails, "which step" is the whole of the diagnosis.
 */

type Step = { step: number | string; label: string; result: string };

export const ProvisionButton = ({ record }: { record: any }) => {
    const notify = useNotify();
    const refresh = useRefresh();

    const [open, setOpen] = useState(false);
    const [running, setRunning] = useState(false);
    const [steps, setSteps] = useState<Step[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [log, setLog] = useState<string[]>([]);
    const [demoData, setDemoData] = useState(false);

    if (!record) return null;

    const alreadyActive = record.provision_state === 'active';

    const run = async () => {
        setRunning(true);
        setError(null);
        setSteps([]);
        setLog([]);

        try {
            const token =
                localStorage.getItem('jwt_access_token') || localStorage.getItem('token');

            const res = await fetch(`${APIUrl}school/provision/${record.id}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ demo_data: demoData }),
            });
            const json = await res.json();

            if (json.status === 'success') {
                setSteps(json.result.steps || []);
                setLog(json.result.migration_log || []);
                notify(json.message, { type: 'success' });
                refresh();
            } else {
                // The message carries the failing step and what to do about it;
                // showing it verbatim beats summarising it into uselessness.
                setError(json.message || 'Provisioning failed.');
                setLog((json.result && json.result.migration_log) || []);
                notify('Provisioning failed', { type: 'error' });
                refresh();
            }
        } catch (e: any) {
            setError(e.message || String(e));
            notify('Provisioning failed', { type: 'error' });
        } finally {
            setRunning(false);
        }
    };

    return (
        <>
            <Button
                variant="contained"
                color={alreadyActive ? 'inherit' : 'primary'}
                onClick={() => setOpen(true)}
            >
                {alreadyActive ? 'Re-run provisioning' : 'Provision database'}
            </Button>

            <Dialog open={open} onClose={() => !running && setOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    Provision {record.code} &mdash; {record.name}
                </DialogTitle>

                <DialogContent>
                    {steps.length === 0 && !error && (
                        <>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                This creates the database <b>wizi_school_
                                {String(record.id).padStart(4, '0')}</b>, a MySQL account that can
                                reach only that database, applies the tenant migrations, and
                                verifies that a real request binds to it.
                            </Typography>

                            {alreadyActive && (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    This school is already provisioned and serving. Running again is
                                    safe &mdash; every step resumes rather than rebuilding. No data
                                    is lost.
                                </Alert>
                            )}

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={demoData}
                                        onChange={e => setDemoData(e.target.checked)}
                                    />
                                }
                                label="Also seed one academic year and three sample sections (11-A, 11-B, 12-A)"
                            />
                        </>
                    )}

                    {error && (
                        <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                            {error}
                        </Alert>
                    )}

                    {steps.length > 0 && (
                        <Table size="small">
                            <TableBody>
                                {steps.map((s, i) => (
                                    <TableRow key={i}>
                                        <TableCell width={40}>{s.step}</TableCell>
                                        <TableCell>{s.label}</TableCell>
                                        <TableCell sx={{ color: 'success.main' }}>
                                            {s.result}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}

                    {log.length > 0 && (
                        <Typography
                            component="pre"
                            variant="caption"
                            sx={{
                                mt: 2,
                                p: 1,
                                bgcolor: 'action.hover',
                                borderRadius: 1,
                                overflowX: 'auto',
                            }}
                        >
                            {log.join('\n')}
                        </Typography>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpen(false)} disabled={running}>
                        Close
                    </Button>
                    {steps.length === 0 && (
                        <Button variant="contained" onClick={run} disabled={running}>
                            {running ? <CircularProgress size={20} /> : 'Run provisioning'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
};
